import { useState, useEffect } from 'react';

interface NavigationProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export default function Navigation({ mobileMenuOpen, setMobileMenuOpen }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0f]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img src="/logo.png" alt="Kuze Logo" className="h-10 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('games')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              Games
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              How it works
            </button>
            <button onClick={() => scrollToSection('packs')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              Packs
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              FAQ
            </button>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a href="https://apps.apple.com/app/kuze/id6742669797" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8B5CF6] to-[#A419E7] rounded-lg text-white hover:opacity-90 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-apple-fill text-lg"></i>
              <div className="flex flex-col items-start">
                <span className="text-[10px] font-normal opacity-90 leading-tight">Download on the</span>
                <span className="text-sm font-semibold leading-tight">App Store</span>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=app.kuze.game" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#606FE5] to-[#4758E0] rounded-lg text-white hover:opacity-90 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-google-play-fill text-lg"></i>
              <div className="flex flex-col items-start">
                <span className="text-[10px] font-normal opacity-90 leading-tight">Get it on</span>
                <span className="text-sm font-semibold leading-tight">Google Play</span>
              </div>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('games')} className="text-gray-300 hover:text-white transition-colors text-left cursor-pointer">
                Games
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white transition-colors text-left cursor-pointer">
                How it works
              </button>
              <button onClick={() => scrollToSection('packs')} className="text-gray-300 hover:text-white transition-colors text-left cursor-pointer">
                Packs
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-white transition-colors text-left cursor-pointer">
                FAQ
              </button>
              <a href="https://apps.apple.com/app/kuze/id6742669797" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#8B5CF6] to-[#A419E7] rounded-xl text-white whitespace-nowrap cursor-pointer">
                <i className="ri-apple-fill text-xl"></i>
                <div className="flex flex-col items-start">
                  <span className="text-xs font-normal opacity-90">Download on the</span>
                  <span className="text-base font-semibold leading-tight">App Store</span>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=app.kuze.game" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#606FE5] to-[#4758E0] rounded-xl text-white whitespace-nowrap cursor-pointer">
                <i className="ri-google-play-fill text-xl"></i>
                <div className="flex flex-col items-start">
                  <span className="text-xs font-normal opacity-90">Get it on</span>
                  <span className="text-base font-semibold leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
